import { Logo } from "components/shopify/logo/logo";
import { history } from "mobx-stores";
import { useRootStore } from "mobx-stores/store-provider";
import React, { useState } from "react";
import { ShopifyRegisterPayload } from "services";
import validate from "validate.js";
import styles from "./shopify-signup-page.module.scss";

interface FormErrors {
  firstName?: string;
  emailAddress?: string;
  password?: string;
  confirmPassword?: string;
  companyName?: string;
  shopifySiteUrl?: string;
}

const shopifyRegisterFormConstraints = {
  firstName: {
    presence: true,
    length: {
      minimum: 2,
    },
  },
  emailAddress: {
    presence: true,
    format: {
      pattern:
        /^[a-z0-9][a-z0-9-_.]+@([a-z]|[a-z0-9]?[a-z0-9-]+[a-z0-9])\.[a-z0-9]{2,10}(?:\.[a-z]{2,10})?$/,
    },
  },
  password: {
    presence: true,
    length: {
      minimum: 2,
    },
  },
  companyName: {
    presence: true,
    length: {
      minimum: 2,
    },
  },
  shopifySiteUrl: {
    presence: true,
    format: {
      pattern: /^https?:\/\/.+\..+/,
    },
  },
};

const ShopifySignupPage: React.FC = () => {
  const {
    generalStore: { api },
  } = useRootStore();

  const [formState, setFormState] = useState<ShopifyRegisterPayload>({
    firstName: "",
    emailAddress: "",
    password: "",
    companyName: "",
    shopifySiteUrl: "",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendVerifyOtp = async (email: string) => {
    try {
      const response = await fetch(
        `https://dev-api-cluster.system.touchetv.com/backoffice-user/api/v1/MailSend`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ emailAddress: email }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send OTP. Please try again.");
      }

      localStorage.setItem("emailAddress", email);
      history.push("/delete-account");
    } catch (err) {
      console.error("Error sending OTP:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(
      formState,
      shopifyRegisterFormConstraints
    );
    if (validationErrors) {
      const formattedErrors: FormErrors = {};
      Object.keys(validationErrors).forEach((key) => {
        formattedErrors[key as keyof FormErrors] = validationErrors[key][0];
      });
      setErrors(formattedErrors);
      return;
    }

    // Check if passwords match
    if (formState.password !== confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const authPayload = await api.shopifyApi.register(formState);
      if (authPayload) {
        await sendVerifyOtp(formState.emailAddress);

        const url = "https://touche-studio-dev.system.touchetv.com/verify-otp";
        const urlObject = new URL(url);
        urlObject.searchParams.set("email", formState.emailAddress);
        window.location.href = urlObject.toString();
      }
    } catch (error) {
      console.error("Sign up error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (
    field: keyof ShopifyRegisterPayload,
    value: string
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
    // Clear error if passwords now match
    if (value === formState.password && errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
  };

  return (
    <div className={styles.signupPage}>
      <div className={styles.signupContainer}>
        <form className={styles.signupForm} onSubmit={handleSubmit}>
          <div className={styles.signupCard}>
            <div className={styles.logoContainer}>
              <Logo />
            </div>
            <div className={styles.title}>
              Shopify Admin Signup
            </div>
            <div className={styles.inputGroup}>
              <label
                htmlFor="first-name"
                className={styles.label}
              >
                First Name
              </label>
              <input
                id="first-name"
                name="firstName"
                type="text"
                autoComplete="given-name"
                required
                value={formState.firstName}
                onChange={(e) => {
                  handleFieldChange("firstName", e.target.value);
                }}
                className={errors.firstName ? styles.inputError : styles.input}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <p className={styles.errorMessage}>{errors.firstName}</p>
              )}
            </div>
            <div className={styles.inputGroup}>
              <label
                htmlFor="email-address"
                className={styles.label}
              >
                Email Address
              </label>
              <input
                id="email-address"
                name="emailAddress"
                type="email"
                autoComplete="email"
                required
                value={formState.emailAddress}
                onChange={(e) => {
                  handleFieldChange("emailAddress", e.target.value);
                }}
                className={errors.emailAddress ? styles.inputError : styles.input}
                placeholder="Enter your email"
              />
              {errors.emailAddress && (
                <p className={styles.errorMessage}>
                  {errors.emailAddress}
                </p>
              )}
            </div>
            <div className={styles.inputGroup}>
              <label
                htmlFor="password"
                className={styles.label}
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={formState.password}
                onChange={(e) => {
                  handleFieldChange("password", e.target.value);
                  // Clear confirm password error if passwords now match
                  if (
                    e.target.value === confirmPassword &&
                    errors.confirmPassword
                  ) {
                    setErrors((prev) => ({
                      ...prev,
                      confirmPassword: undefined,
                    }));
                  }
                }}
                className={errors.password ? styles.inputError : styles.input}
                placeholder="Enter your password"
              />
              {errors.password && (
                <p className={styles.errorMessage}>{errors.password}</p>
              )}
            </div>
            <div className={styles.inputGroup}>
              <label
                htmlFor="confirm-password"
                className={styles.label}
              >
                Confirm Password
              </label>
              <input
                id="confirm-password"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => {
                  handleConfirmPasswordChange(e.target.value);
                }}
                className={errors.confirmPassword ? styles.inputError : styles.input}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && (
                <p className={styles.errorMessage}>
                  {errors.confirmPassword}
                </p>
              )}
            </div>
            <div className={styles.inputGroup}>
              <label
                htmlFor="company-name"
                className={styles.label}
              >
                Company Name
              </label>
              <input
                id="company-name"
                name="companyName"
                type="text"
                required
                value={formState.companyName}
                onChange={(e) => {
                  handleFieldChange("companyName", e.target.value);
                }}
                className={errors.companyName ? styles.inputError : styles.input}
                placeholder="Enter your company name"
              />
              {errors.companyName && (
                <p className={styles.errorMessage}>
                  {errors.companyName}
                </p>
              )}
            </div>
            <div className={styles.inputGroup}>
              <label
                htmlFor="shopify-site-url"
                className={styles.label}
              >
                Shopify Site URL
              </label>
              <input
                id="shopify-site-url"
                name="shopifySiteUrl"
                type="url"
                required
                value={formState.shopifySiteUrl}
                onChange={(e) => {
                  handleFieldChange("shopifySiteUrl", e.target.value);
                }}
                className={errors.shopifySiteUrl ? styles.inputError : styles.input}
                placeholder="https://www.yourshop.com"
              />
              {errors.shopifySiteUrl && (
                <p className={styles.errorMessage}>
                  {errors.shopifySiteUrl}
                </p>
              )}
            </div>
            <div className={styles.signinLink}>
              <span>Or</span>{" "}
              <button
                type="button"
                onClick={() => history.push("/shopify/login")}
                className={styles.signinLinkText}
              >
                sign in to your existing account
              </button>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? "Creating account..." : "Sign up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { ShopifySignupPage };
